import { useNavigate } from "@tanstack/react-router";
import { z } from "zod";

import instance from "@/lib/axios";
import { getApprovalUrl, isSupervisorUser } from "@/lib/supervisor";
import useMarkNotificationRead from "@/hooks/api/notifications/useMarkNotificationRead";
import { useMyPermissions } from "@/hooks/api/authorization/useAuthorization";
import { isAdmin as canManageRoles } from "@/lib/permissions";
import { useFeatureStore } from "@/stores/useFeatureStore";
import type { Notification } from "@/types/notification";

/** The module a notification's record belongs to. */
const RESOURCE_FEATURE: Record<string, string> = {
  leave_request: "leave",
  task: "tasks",
};

/**
 * Shared "open a notification" behaviour: mark it read (if unread), then open
 * the relevant record on the page appropriate to the viewer's role.
 *
 * One notification row fans out to a mixed audience (owner + supervisor chain +
 * HR) living under different route prefixes, so the destination is derived here
 * from the viewer's role + the notification's `resource_type`/`resource_id`
 * (the backend intentionally does not store a per-recipient URL). Navigation is
 * wrapped defensively: an unknown type or a stale id degrades to "mark read,
 * stay put".
 */
export function useNotificationClick(onNavigate?: () => void) {
  const navigate = useNavigate();
  const markRead = useMarkNotificationRead();
  const { data: capabilities } = useMyPermissions();

  return async (notification: Notification) => {
    if (!notification.is_read) {
      markRead.mutate(notification.id);
    }

    const { resource_type, resource_id } = notification;
    if (resource_id == null) return;

    const isAdmin = canManageRoles(capabilities);
    const isSupervisor = isSupervisorUser(capabilities);

    // The self-service pages for a module the org hasn't enabled, or this
    // role blocks, would only bounce to /unauthorized — stay put instead.
    const feature = RESOURCE_FEATURE[resource_type];
    if (!isAdmin && feature && !useFeatureStore.getState().isEnabled(feature)) {
      return;
    }

    try {
      if (resource_type === "leave_request") {
        if (isAdmin) {
          await navigate({
            to: "/admin/leave/requests",
            search: { leaveRequestId: resource_id },
          });
        } else if (isSupervisor) {
          // Resolve the wrapping approval from the leave-request id; when one
          // exists (supervisor-approval org) open its detail page, else fall
          // back to the member sheet.
          const approvalId = await resolveApprovalId(resource_id);
          if (approvalId != null) {
            await navigate({
              to: "/supervisor/approvals/$id",
              params: { id: String(approvalId) },
            });
          } else {
            await navigate({
              to: "/user/leave",
              search: { leaveRequestId: resource_id },
            });
          }
        } else {
          await navigate({
            to: "/user/leave",
            search: { leaveRequestId: resource_id },
          });
        }
      } else if (resource_type === "task") {
        if (isAdmin) {
          await navigate({
            to: "/admin/tasks",
            search: { taskId: resource_id },
          });
        } else {
          await navigate({
            to: "/user/my-tasks",
            search: { taskId: resource_id },
          });
        }
      } else if (isInAppPath(notification.redirect_url)) {
        // Overtime and any other type the server addresses itself: it fills
        // `redirect_url` for the recipient's area.
        await navigate({ href: notification.redirect_url });
      } else {
        return;
      }
      onNavigate?.();
    } catch {
      // Target route may not exist yet / id is stale — ignore and stay put.
    }
  };
}

/** Only follow a server-sent redirect that stays inside the app. */
function isInAppPath(url: string | null | undefined): url is string {
  return !!url && url.startsWith("/") && !url.startsWith("//");
}

/** The approvals endpoint answers paginated or as a bare array. */
const approvalRowsSchema = z.union([
  z
    .object({ results: z.array(z.object({ id: z.number() })) })
    .transform((body) => body.results),
  z.array(z.object({ id: z.number() })),
]);

/** Return the id of the LeaveApproval wrapping a leave request, or null. */
async function resolveApprovalId(
  leaveRequestId: number,
): Promise<number | null> {
  try {
    const res = await instance.get(getApprovalUrl("requests/"), {
      params: { leave_request: leaveRequestId },
    });
    const rows = approvalRowsSchema.parse(res.data);
    return rows.length > 0 ? rows[0].id : null;
  } catch {
    return null;
  }
}

export default useNotificationClick;
